/* global $:true */
import debug from "debug";
const log = debug("tree-menu");

import isFocusable from "ally.js/src/is/focusable";
import loadTreeMenu from "./load-tree-menu";

const menuSelector = "[data-tree-menu]";
const menuGroupSelector = "[data-tree-menu-group]";
const menuItemSelector = "[data-tree-menu-item]";
const menuButtonSelector = "[data-tree-menu-button]";
const menuBackButtonSelector = "[data-tree-menu-back]";

export default function () {
    const menuElement = document.querySelector(menuSelector);
    if (!menuElement) {
        log("No tree-menu found. Aborting.");
        return false;
    }

    const parentMenu = menuElement.closest(
        menuElement.dataset.treeMenuMegaSelector
    );
    if (!parentMenu) {
        log(
            `No parent menu found with selector: data-tree-menu-mega-selector="${menuElement.dataset.treeMenuMegaSelector}". Aborting.`
        );
        return false;
    }

    // Initialise upon first mega menu open
    // parentMenu.addEventListener("menu_opened", init, {
    //     once: true
    // });

    // Fetch menu returning promise when done
    const loadUrl = menuElement.dataset.treeMenuLoadUrl;
    const loadTarget = menuElement.querySelector(
        menuElement.dataset.treeMenuLoadTargetSelector
    );
    const errorMessage = menuElement.querySelector(
        menuElement.dataset.treeMenuErrorMessageSelector
    );
    const loadingIndicator = menuElement.querySelector(
        menuElement.dataset.treeMenuLoadingIndicatorSelector
    );

    loadTreeMenu(loadUrl, loadTarget).then(()=>{
        treeMenu(menuElement);
    }).catch(()=>{
        log("tree menu error");
        loadingIndicator.style.display = "none";
        errorMessage.style.display = "";
    });
}

function treeMenu(element) {
    // todo: consider removing scope - will find first item so okay?
    const topGroupElement = element.querySelector(
        `:scope > ${menuGroupSelector}`
    );

    const observer = new IntersectionObserver(intersectionHandler, {
        root: element,
        threshold: [0, 1],
    });

    const parentMenu = element.closest(element.dataset.treeMenuMegaSelector);

    menuGroup(
        { parentItem: element, observer, rootMenu: element },
        topGroupElement
    );

    // set depth - listening to events from contained buttons - including back button
    element.addEventListener("opened", handleMenuOpen);
    element.addEventListener("closed", handleMenuClosed);
    parentMenu.addEventListener("menu_closed", handleMegaClosed);

    function intersectionHandler(entries) {
        entries.forEach((item) => {
            // The displayed button extends a little to the left to trigger an edge intersection
            if (item.isIntersecting && item.boundingClientRect.x < 0) {
                item.target.disabled = false;
            } else {
                item.target.disabled = true;
            }
        });
    }

    function handleMenuOpen(event) {
        setDepth(element, +event.detail.depth);
    }
    function handleMenuClosed(event) {
        setDepth(element, +event.detail.depth - 1);
    }
    function handleMegaClosed() {
        setDepth(element, 0);
        element.dispatchEvent(
            new CustomEvent("parent_closed", {
                bubbles: false,
            })
        );
    }
}

function menuGroup({ observer, rootMenu, parentItem }, groupElement) {
    // find/init contained menu items
    const menuItemElements = [
        ...groupElement.querySelectorAll(`:scope > ${menuItemSelector}`),
    ];

    const backButton = groupElement.querySelector(
        `:scope > ${menuBackButtonSelector}`
    );

    const menuItems = menuItemElements.map(
        menuItem.bind(null, { observer, rootMenu })
    );

    // Top menu has no back button
    if (backButton) {
        observer.observe(backButton);
        backButton.addEventListener("click", handleBackClick);
    }

    // For top product menu the parent tree menu
    if (parentItem) {
        parentItem.addEventListener(
            "parent_closed",
            handleParentClosed.bind(null, menuItems)
        );
        parentItem.addEventListener("parent_opened", () => {
            setTimeout(
                (backButton, isFocusable) => {
                    if (backButton && isFocusable(backButton)) {
                        backButton.focus();
                    }
                },
                50,
                backButton,
                isFocusable
            );
        });
    }

    // close other child items when child item opened
    // deepest level has no menu items (just links)
    if (menuItems.length) {
        groupElement.addEventListener(
            "opened",
            handleItemOpened.bind(null, { menuItems, backButton })
        );
    }

    function handleBackClick() {
        groupElement.dispatchEvent(
            new CustomEvent("back", {
                bubbles: true,
            })
        );
    }

    function handleParentClosed(items) {
        items.forEach((item) => {
            item.dataset.treeMenuItemOpened = false;
        });
    }
}

function menuItem({ observer, rootMenu }, element) {
    // find and init contained menuGroup if exists
    // (iterate into subsequent nested items and groups)
    // consider removing scope - first found element with be okay?
    const childGroup = element.querySelector(
        `:scope > * > ${menuGroupSelector}`
    );
    if (childGroup) {
        menuGroup({ observer, rootMenu, parentItem: element }, childGroup);
    }

    // consider removing scope - first found element with be okay?
    const button = element.querySelector(`:scope > ${menuButtonSelector}`);

    const isMenuItemOpened = isOpened.bind(null, element);

    // View has expanded attr already set
    // button.setAttribute("aria-expanded", "false");

    button.addEventListener("click", handleButtonClick);
    element.addEventListener("back", handleBackEvent);

    // mutation observer updates button expanded state based on data attr
    // Parent component updates data attr
    const attributeObserver = new MutationObserver(attributeChangeHandler);
    attributeObserver.observe(element, { attributes: true });

    function handleButtonClick() {
        const action = isMenuItemOpened() ? buttonClose : buttonOpen;
        action();
    }

    function handleBackEvent(event) {
        // Immediate child menu events only
        if (event.target !== childGroup) {
            return false;
        }
        buttonClose();
        button.focus();
    }

    // emit opened events - bubble to parent group
    function buttonOpen() {
        element.dataset.treeMenuItemOpened = true;
        element.dispatchEvent(
            new CustomEvent("opened", {
                bubbles: true,
                detail: { depth: element.dataset.treeMenuItemDepth },
            })
        );
    }

    function buttonClose() {
        element.dataset.treeMenuItemOpened = false;
        // emit closed events - listened to by parent menu
        element.dispatchEvent(
            new CustomEvent("closed", {
                bubbles: true,
                detail: { depth: element.dataset.treeMenuItemDepth },
            })
        );
    }

    function attributeChangeHandler(mutationList, observer) {
        mutationList
            .filter((mutation) => {
                return (
                    mutation.type === "attributes" &&
                    mutation.attributeName.startsWith(
                        "data-tree-menu-item-opened"
                    )
                );
            })
            .forEach(updateButton);
    }

    function updateButton() {
        button.setAttribute("aria-expanded", isMenuItemOpened());

        const eventName = isMenuItemOpened()
            ? "parent_opened"
            : "parent_closed";
        // Direct listeners only i.e. contained menu group
        element.dispatchEvent(
            new CustomEvent(eventName, {
                bubbles: false,
            })
        );
    }

    // Return an element hook to parent group
    return element;
}

function isOpened(menuItem) {
    return menuItem.dataset.treeMenuItemOpened === "true";
}

function setDepth(element, depth) {
    element.dataset.treeMenuOpenDepth = depth;
}

function handleItemOpened({ menuItems, backButton }, event) {
    // Check if event target is a child menu item (not grandchild)
    if (!menuItems.includes(event.target)) {
        return true;
    }

    const siblingItems = menuItems.filter((item) => {
        return item !== event.target;
    });

    // close siblings
    siblingItems.forEach((item) => {
        item.dataset.treeMenuItemOpened = false;
    });
}



// WEBPACK FOOTER //
// ./project/client/src/core/modules/tree-menu/tree-menu.js